import { Button, Checkbox, Css, HasIdAndName, MultiSelectField, NestedOption, TreeSelectField } from "@homebound/beam";
import { useMemo } from "react";
import { BusinessFunctionDetails } from "src/components/change-log/components/BusinessFunctionAssets";
import { CustomTaskCatalogPageFilter } from "../TaskCatalogPage";
import { TaskCatalogPageMetadata_RegionFragment } from "../TaskCatalogPage.generated";

type TaskCatalogFilterModalProps = {
  businessFunctionTypes: BusinessFunctionDetails[];
  regions: TaskCatalogPageMetadata_RegionFragment[];
  globalPlanTags: HasIdAndName[];
  filter: CustomTaskCatalogPageFilter;
  setFilter: (filter: CustomTaskCatalogPageFilter) => void;
};

export function TaskCatalogFilterModal(props: TaskCatalogFilterModalProps) {
  const { filter, setFilter } = props;
  const { teams, regions, tags } = useMemo(
    () => ({
      teams: props.businessFunctionTypes.sortByKey("name"),
      regions: getSpecifyToTreeSelectOptions(props.regions),
      tags: props.globalPlanTags.sortByKey("name"),
    }),
    [props.businessFunctionTypes, props.regions, props.globalPlanTags],
  );
  return (
    <div css={Css.df.fdc.gap2.$}>
      <MultiSelectField
        onSelect={(val) => {
          setFilter({ ...filter, team: val.isEmpty ? undefined : val });
        }}
        label="Team"
        options={teams}
        getOptionLabel={({ name }) => name}
        getOptionValue={({ code }) => code}
        values={filter.team ?? []}
      />
      <TreeSelectField
        options={regions}
        label="Specific to"
        values={filter.project ?? []}
        defaultCollapsed
        onSelect={(val) => {
          setFilter({ ...filter, project: val.all.values.isEmpty ? undefined : (val.all.values as string[]) });
        }}
      />
      <MultiSelectField
        onSelect={(val) => {
          setFilter({ ...filter, tags: val.isEmpty ? undefined : val });
        }}
        label="Tags"
        options={tags}
        getOptionLabel={({ name }) => name}
        getOptionValue={({ id }) => id}
        values={filter.tags ?? []}
      />
      <Checkbox
        label="Specific to All Schedules"
        selected={!!filter.applyToAllSchedules}
        onChange={(val) => {
          setFilter({ ...filter, applyToAllSchedules: val });
        }}
      />
      <Checkbox
        label="Show Archived Tasks"
        selected={!!filter.archived}
        onChange={(val) => {
          setFilter({ ...filter, archived: val });
        }}
      />
      {Object.keys(filter).length > 0 && <Button label="Clear" onClick={() => setFilter({})} variant="text" />}
    </div>
  );
}

function getSpecifyToTreeSelectOptions(
  regions: TaskCatalogPageMetadata_RegionFragment[],
): NestedOption<HasIdAndName>[] {
  return regions
    .map((r) => ({
      ...r,
      children: r.markets.map((m) => ({
        ...m,
        children: m.communitites.map((c) => ({ ...c, children: c.subdivisions })),
      })),
    }))
    .sortBy((r) => r.name);
}
